import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Mail, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { BottomNav } from '../components/BottomNav';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/support/contact`, {
        subject: subject.trim(),
        message: message.trim(),
        email: user?.email
      });
      setTicketId(response.data.ticket_id);
      setSubmitted(true);
      toast.success('Support ticket submitted!');
    } catch (error) {
      toast.error('Failed to submit ticket');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background pb-nav">
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="flex items-center gap-3 px-4 py-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings')}
              className="rounded-full"
            >
              <ArrowLeft size={20} />
            </Button>
            <h1 className="font-heading font-bold text-lg">Contact Support</h1>
          </div>
        </header>

        <main className="px-4 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="p-4 rounded-full bg-[#8BB89F]/20 w-fit mx-auto mb-6">
              <CheckCircle size={48} className="text-[#8BB89F]" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-2">Ticket Submitted!</h2>
            <p className="text-muted-foreground mb-2">
              Your ticket ID is:
            </p>
            <p className="font-mono text-lg font-bold text-primary mb-6">{ticketId}</p>
            <p className="text-sm text-muted-foreground mb-8">
              We'll get back to you within 24-48 hours via email.
            </p>
            <Button
              onClick={() => navigate('/settings')}
              className="bg-primary hover:bg-primary/90 rounded-full"
            >
              Back to Settings
            </Button>
          </motion.div>
        </main>

        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="font-heading font-bold text-lg">Contact Support</h1>
        </div>
      </header>

      <main className="px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Info Card */}
          <Card className="glass-card p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <Mail size={20} className="text-primary" />
              </div>
              <div>
                <p className="font-medium">Email Support</p>
                <p className="text-sm text-muted-foreground">We typically respond within 24-48 hours</p>
                <a href="mailto:hello@healthlink360.ai" className="text-primary text-sm hover:underline">
                  hello@healthlink360.ai
                </a>
              </div>
            </div>
          </Card>

          {/* Contact Form */}
          <Card className="glass-card p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Your Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="rounded-xl h-12 bg-muted"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder="What can we help you with?"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="rounded-xl h-12"
                  data-testid="subject-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  placeholder="Describe your issue or question in detail..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="rounded-xl resize-none"
                  data-testid="message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !subject.trim() || !message.trim()}
                className="w-full bg-primary hover:bg-primary/90 rounded-full py-6"
                data-testid="submit-ticket-btn"
              >
                {loading ? 'Submitting...' : (
                  <>
                    <Send size={18} className="mr-2" />
                    Submit Ticket
                  </>
                )}
              </Button>
            </form>
          </Card>
        </motion.div>
      </main>

      <BottomNav />
    </div>
  );
}
